import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Settings, LogOut, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Account() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getSubscriptionStatus = () => {
    if (!user.subscription) {
      return {
        label: 'No Active Subscription',
        color: 'text-neutral-400',
        bgColor: 'bg-neutral-900',
        icon: XCircle,
      };
    }

    switch (user.subscription.status) {
      case 'active':
        return {
          label: 'Active',
          color: 'text-green-400',
          bgColor: 'bg-green-950/30',
          icon: CheckCircle2,
        };
      case 'trialing':
        return {
          label: 'Trial',
          color: 'text-blue-400',
          bgColor: 'bg-blue-950/30',
          icon: Clock,
        };
      case 'past_due':
        return {
          label: 'Past Due',
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-950/30',
          icon: Clock,
        };
      case 'canceled':
        return {
          label: 'Canceled',
          color: 'text-red-400',
          bgColor: 'bg-red-950/30',
          icon: XCircle,
        };
      default:
        return {
          label: user.subscription.status,
          color: 'text-neutral-400',
          bgColor: 'bg-neutral-900',
          icon: Clock,
        };
    }
  };

  const getPlanName = (plan) => {
    const plans = {
      core: 'CORE',
      pro: 'PRO',
      elite: 'ELITE',
    };
    return plans[plan] || plan?.toUpperCase() || 'Unknown';
  };

  const statusInfo = getSubscriptionStatus();
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Header */}
      <nav className="border-b border-neutral-800 bg-[#111315]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/logo.png" alt="HDAlgos logo" className="h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>
            <Link to="/" className="text-sm text-neutral-400 hover:text-white transition flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-2">Account Dashboard</h1>
          <p className="text-neutral-400">Manage your subscription and account settings</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Subscription Info */}
            <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
              <h2 className="text-2xl font-black mb-6">Subscription</h2>

              {user.subscription ? (
                <div className="space-y-6">
                  {/* Status Badge */}
                  <div className="flex items-center gap-3">
                    <div className={`${statusInfo.bgColor} ${statusInfo.color} px-4 py-2 rounded-lg flex items-center gap-2`}>
                      <StatusIcon className="w-5 h-5" />
                      <span className="font-bold">{statusInfo.label}</span>
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-4 border-b border-neutral-800">
                      <span className="text-neutral-400">Current Plan</span>
                      <span className="text-[#CBB26A] font-black text-xl">
                        {getPlanName(user.subscription.plan)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center pb-4 border-b border-neutral-800">
                      <span className="text-neutral-400">Billing Cycle</span>
                      <span className="font-bold capitalize">{user.subscription.billingCycle}</span>
                    </div>

                    {user.subscription.currentPeriodEnd && (
                      <div className="flex justify-between items-center pb-4 border-b border-neutral-800">
                        <span className="text-neutral-400">
                          {user.subscription.cancelAtPeriodEnd ? 'Expires On' : 'Next Billing Date'}
                        </span>
                        <span className="font-bold">
                          {new Date(user.subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    )}

                    {user.subscription.cancelAtPeriodEnd && (
                      <div className="bg-yellow-950/30 ring-1 ring-yellow-900/50 rounded-lg p-4">
                        <p className="text-yellow-400 text-sm">
                          Your subscription will be canceled at the end of the current billing period.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <Link
                    to="/account/billing"
                    className="block w-full text-center bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-3 rounded-lg font-bold hover:from-[#D8C585] hover:to-[#BDA45E] transition"
                  >
                    Manage Billing
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <XCircle className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                  <p className="text-neutral-400 mb-6">You don't have an active subscription</p>
                  <Link
                    to="/#pricing"
                    className="inline-block bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-8 py-3 rounded-lg font-bold hover:from-[#D8C585] hover:to-[#BDA45E] transition"
                  >
                    View Plans
                  </Link>
                </div>
              )}
            </div>

            {/* Profile Info */}
            <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8">
              <h2 className="text-2xl font-black mb-6">Profile Information</h2>

              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-neutral-800">
                  <span className="text-neutral-400">Full Name</span>
                  <span className="font-bold">{user.fullName || 'Not set'}</span>
                </div>

                <div className="flex justify-between items-center pb-4 border-b border-neutral-800">
                  <span className="text-neutral-400">Email</span>
                  <span className="font-bold">{user.email}</span>
                </div>

                <div className="flex justify-between items-center pb-4 border-b border-neutral-800">
                  <span className="text-neutral-400">TradingView Username</span>
                  <span className="font-bold">{user.tradingViewId || 'Not set'}</span>
                </div>
              </div>

              <Link
                to="/account/settings"
                className="block w-full text-center mt-6 bg-neutral-900 ring-1 ring-neutral-800 text-white py-3 rounded-lg font-bold hover:ring-[#CBB26A] hover:text-[#CBB26A] transition"
              >
                Edit Profile
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-6">
              <h3 className="text-lg font-black mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link
                  to="/account/settings"
                  className="flex items-center gap-3 p-3 rounded-lg bg-neutral-900 hover:ring-1 hover:ring-[#CBB26A] transition group"
                >
                  <Settings className="w-5 h-5 text-neutral-400 group-hover:text-[#CBB26A]" />
                  <span className="font-bold">Account Settings</span>
                </Link>

                {user.subscription && (
                  <Link
                    to="/account/billing"
                    className="flex items-center gap-3 p-3 rounded-lg bg-neutral-900 hover:ring-1 hover:ring-[#CBB26A] transition group"
                  >
                    <CreditCard className="w-5 h-5 text-neutral-400 group-hover:text-[#CBB26A]" />
                    <span className="font-bold">Billing & Plans</span>
                  </Link>
                )}

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-neutral-900 hover:ring-1 hover:ring-red-500 transition group"
                >
                  <LogOut className="w-5 h-5 text-neutral-400 group-hover:text-red-400" />
                  <span className="font-bold group-hover:text-red-400">Sign Out</span>
                </button>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-6">
              <h3 className="text-lg font-black mb-4">Account Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-400">User ID</span>
                  <span className="font-mono text-xs text-neutral-500">{user.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">Account Type</span>
                  <span className="font-bold text-[#CBB26A]">Individual</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
